import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, LayoutGrid, Folder, GripVertical, Home, X, BarChart3, ChevronDown, ChevronRight, ChevronUp, UserCircle } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { usePreferences, getUserActiveModuleCodes } from '../../context/PreferencesContext';
import { useModuleCounts } from '../../hooks/useModuleCounts';
import {
    reorderGroupModules,
    getNavToModuleMap,
    getModuleNavMap,
    getModuleIdMap,
    getModulesList,
    getModuleByCode,
    NAV_TO_MODULE_MAP
} from '../../services/modulesService';
import { getIconByName, findModuleByNavId, findModuleByCode } from '../../utils/iconUtils';

/**
 * Menu Component
 * Displays user info, navigation menu, pinned modules/groups, and logout button
 *
 * @param {Object} props
 * @param {string} props.currentPage - Current active page
 * @param {Function} props.onNavigate - Navigation handler
 * @param {Function} props.onLogout - Logout handler
 */
const Menu = ({ currentPage, onNavigate, onLogout }) => {
    const { t, language } = useTranslation();
    const { currentTheme } = useTheme();

    // Use centralized preferences context
    const {
        pinnedItems,
        updatePinnedItems,
        collapsedSidebarGroups,
        updateCollapsedSidebarGroups,
        moduleGroups: contextModuleGroups,
        updateModuleGroups,
        refreshModuleGroups,
        isLoaded: preferencesLoaded
    } = usePreferences();

    // Helper to normalize object keys to lowercase (API returns uppercase)
    const normalizeKeys = (obj) => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(normalizeKeys);
        return Object.keys(obj).reduce((acc, key) => {
            const lowerKey = key.toLowerCase();
            acc[lowerKey] = normalizeKeys(obj[key]);
            return acc;
        }, {});
    };

    // Module counts for badges (from useModuleCounts hook)
    const moduleCounts = useModuleCounts();


    // Legacy getters for compatibility
    const pinnedModules = pinnedItems.filter(i => i.type === 'module').map(i => i.id);
    const pinnedGroups = pinnedItems.filter(i => i.type === 'group').map(i => i.id);

    // Groups data from API (cached in localStorage)
    const [groupsData, setGroupsData] = useState(() => {
        const stored = localStorage.getItem('cabazaGroupsData');
        return stored ? JSON.parse(stored) : [];
    });

    // Drag over state for drop zone
    const [isDragOver, setIsDragOver] = useState(false);

    // Drag state for reordering pinned items
    const [draggedPinnedItem, setDraggedPinnedItem] = useState(null);
    const [dragOverPinnedIndex, setDragOverPinnedIndex] = useState(null);
    const [dragOverGroupId, setDragOverGroupId] = useState(null);

    // Drag state for reordering modules within a group
    const [draggedGroupModule, setDraggedGroupModule] = useState(null); // { groupId, moduleIndex, module }
    const [dragOverGroupModuleItemId, setDragOverGroupModuleItemId] = useState(null);

    // Track if there's an external module drag (from Modules page)
    const [isExternalModuleDrag, setIsExternalModuleDrag] = useState(false);

    // Force re-render when home screen order changes
    const [, setOrderVersion] = useState(0);

    // Group popup state - which group is open in the popup overlay
    const [openGroupPopup, setOpenGroupPopup] = useState(null);

    // Module visibility from localStorage (dynamic from DB)
    const [enabledModules, setEnabledModules] = useState(() => {
        // Get all modules from DB (loaded at login)
        const allModulesList = getModulesList();
        // Build default object with all modules enabled
        const defaultModules = {};
        allModulesList.forEach(m => {
            const navId = (m.navid || m.NAVID || m.code || m.CODE || '').toLowerCase();
            if (navId) defaultModules[navId] = true;
        });
        // Merge with user preferences (which may have some set to false)
        const stored = localStorage.getItem('cabazaModules');
        return stored ? { ...defaultModules, ...JSON.parse(stored) } : defaultModules;
    });

    const userId = parseInt(localStorage.getItem('userId'), 10);

    // All modules from API (with localStorage as initial cache)
    const [allModules, setAllModules] = useState(() => getModulesList());

    // Get module definition dynamically from DB data
    const getModuleDef = (navId) => {
        const moduleData = findModuleByNavId(navId, allModules);
        if (!moduleData) return null;

        const iconName = moduleData.icon || moduleData.ICON;
        const code = (moduleData.code || moduleData.CODE || '').toUpperCase();
        const Icon = getIconByName(iconName || code, LayoutGrid);
        const langKey = `name_${language}`;
        const label = moduleData[langKey] || moduleData.name_fr || moduleData.NAME_FR || moduleData.code || navId;

        return { icon: Icon, label, code };
    };

    // Track last loaded language to avoid duplicate API calls
    const lastLoadedLangRef = useRef(null);
    const lastLoadedModulesLangRef = useRef(null);
    const initialContextLoadedRef = useRef(false);

    // Sync groups data from context - only on initial load
    useEffect(() => {
        if (preferencesLoaded && contextModuleGroups && contextModuleGroups.length > 0 && !initialContextLoadedRef.current) {
            const normalizedData = normalizeKeys(contextModuleGroups);
            setGroupsData(normalizedData);
            lastLoadedLangRef.current = language;
            initialContextLoadedRef.current = true;
        }
    }, [contextModuleGroups, preferencesLoaded, language]);

    // Reload groups on language change
    useEffect(() => {
        if (lastLoadedLangRef.current && lastLoadedLangRef.current !== language) {
            refreshModuleGroups();
        }
    }, [language, refreshModuleGroups]);

    // Load all modules from localStorage (synced at login)
    // Filter to only show modules active for this user's subscription
    useEffect(() => {
        const modulesFromCache = getModulesList();
        if (modulesFromCache && modulesFromCache.length > 0) {
            const activeModuleCodes = getUserActiveModuleCodes();
            const filteredModules = activeModuleCodes.length > 0
                ? modulesFromCache.filter(m => {
                    const code = (m.code || m.CODE || '').toUpperCase();
                    return activeModuleCodes.includes(code);
                })
                : modulesFromCache; // Fallback to all modules if no active modules found
            setAllModules(filteredModules);
        }
    }, []);

    // Listen for subscription changes to refresh allowed modules in toolbar
    useEffect(() => {
        const handleSubscriptionChanged = async () => {
            try {
                const { getModules } = await import('../../services/modulesService');
                const modulesFromApi = await getModules('fr');
                if (modulesFromApi?.length) {
                    localStorage.setItem('cabazaModulesList', JSON.stringify(modulesFromApi));
                    const activeModuleCodes = getUserActiveModuleCodes();
                    const filtered = activeModuleCodes.length > 0
                        ? modulesFromApi.filter(m => activeModuleCodes.includes((m.code || m.CODE || '').toUpperCase()))
                        : modulesFromApi;
                    setAllModules(filtered);
                }
            } catch (err) {
                console.error('[Menu] Error refreshing modules after subscription change:', err);
            }
        };
        window.addEventListener('subscriptionChanged', handleSubscriptionChanged);
        return () => window.removeEventListener('subscriptionChanged', handleSubscriptionChanged);
    }, []);

    // Listen for modules/groups changes from Preferences/Modules
    useEffect(() => {
        const handleModulesGroupsChange = () => {
            // Read from localStorage for immediate update (optimistic)
            // Don't call refreshModuleGroups() immediately - it would overwrite with old API data
            const stored = localStorage.getItem('cabazaGroupsData');
            if (stored) {
                const normalizedData = normalizeKeys(JSON.parse(stored));
                setGroupsData(normalizedData);
            }
        };
        window.addEventListener('moduleGroupsChanged', handleModulesGroupsChange);
        // Also listen for pin toggle events (separate from structural changes)
        window.addEventListener('modulePinnedToggled', handleModulesGroupsChange);
        return () => {
            window.removeEventListener('moduleGroupsChanged', handleModulesGroupsChange);
            window.removeEventListener('modulePinnedToggled', handleModulesGroupsChange);
        };
    }, [userId, language]);

    // Listen for pinned items and group changes (reload groups from preferences cache)
    useEffect(() => {
        const handleGroupsSync = () => {
            // Read groups from cabazaGroupsData first (set by Modules.jsx),
            // then fallback to cabazaPreferencesCache (set by Home.jsx via context)
            const stored = localStorage.getItem('cabazaGroupsData');
            if (stored) {
                setGroupsData(normalizeKeys(JSON.parse(stored)));
            } else {
                try {
                    const cached = localStorage.getItem('cabazaPreferencesCache');
                    if (cached) {
                        const prefs = JSON.parse(cached);
                        if (prefs.moduleGroups) {
                            setGroupsData(normalizeKeys(prefs.moduleGroups));
                        }
                    }
                } catch (e) {
                    console.error('[Menu] Error reading groups from preferences cache:', e);
                }
            }
        };
        window.addEventListener('pinnedItemsChanged', handleGroupsSync);
        window.addEventListener('moduleGroupsChanged', handleGroupsSync);
        // Also listen for legacy events
        window.addEventListener('pinnedModulesChanged', handleGroupsSync);
        window.addEventListener('pinnedGroupsChanged', handleGroupsSync);
        return () => {
            window.removeEventListener('pinnedItemsChanged', handleGroupsSync);
            window.removeEventListener('moduleGroupsChanged', handleGroupsSync);
            window.removeEventListener('pinnedModulesChanged', handleGroupsSync);
            window.removeEventListener('pinnedGroupsChanged', handleGroupsSync);
        };
    }, []);

    // Listen for module visibility changes
    useEffect(() => {
        const handleModulesChange = () => {
            const defaultModules = { contacts: true, map: true, agenda: true, messages: true, events: true, places: true, timeTracking: true, support: true, work: true, offers: true, products: true, expenses: true, books: true, moments: true, carpooling: true, occupationGroups: true, projects: true, meetings: true };
            const stored = localStorage.getItem('cabazaModules');
            setEnabledModules(stored ? { ...defaultModules, ...JSON.parse(stored) } : defaultModules);
        };
        window.addEventListener('modulesChanged', handleModulesChange);
        return () => window.removeEventListener('modulesChanged', handleModulesChange);
    }, []);

    // Listen for home screen order changes (re-sort sidebar to match)
    useEffect(() => {
        const handleOrderChange = () => setOrderVersion(v => v + 1);
        window.addEventListener('dashboardOrderChanged', handleOrderChange);
        return () => window.removeEventListener('dashboardOrderChanged', handleOrderChange);
    }, []);

    // Listen for global dragend to reset external drag state
    useEffect(() => {
        const handleGlobalDragEnd = () => {
            setIsExternalModuleDrag(false);
            setDragOverGroupId(null);
        };
        document.addEventListener('dragend', handleGlobalDragEnd);
        return () => document.removeEventListener('dragend', handleGlobalDragEnd);
    }, []);

    // Handle drag over
    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setIsDragOver(true);
    };

    // Handle drag leave
    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    // Handle drop
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);

        const isGroup = e.dataTransfer.getData('isGroup') === 'true';
        const groupId = e.dataTransfer.getData('groupId');
        const moduleCode = e.dataTransfer.getData('moduleCode');

        if (isGroup && groupId) {
            // Pin group
            const numGroupId = parseInt(groupId, 10);
            if (!pinnedItems.some(i => i.type === 'group' && i.id === numGroupId)) {
                const newPinned = [...pinnedItems, { type: 'group', id: numGroupId }];
                updatePinnedItems(newPinned);
            }
        } else if (moduleCode) {
            // Pin module (convert code to nav id using dynamic map)
            const navMap = getModuleNavMap();
            const moduleId = navMap[moduleCode] || moduleCode.toLowerCase();
            if (!pinnedItems.some(i => i.type === 'module' && i.id === moduleId)) {
                const newPinned = [...pinnedItems, { type: 'module', id: moduleId }];
                updatePinnedItems(newPinned);
            }
        }
    };

    // Toggle sidebar group collapse (persisted via context)
    const toggleSidebarGroup = (groupId) => {
        const newState = {
            ...collapsedSidebarGroups,
            [groupId]: !collapsedSidebarGroups[groupId]
        };
        updateCollapsedSidebarGroups(newState);
    };

    // Handle pinned item drag start (unified for modules and groups)
    const handlePinnedItemDragStart = (e, item, index) => {
        setDraggedPinnedItem({ item, index });
        e.dataTransfer.effectAllowed = 'move';
    };

    // Handle pinned item drag over
    const handlePinnedItemDragOver = (e, index) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverPinnedIndex(index);
        setDragOverGroupId(null);
    };

    // Handle drag over a group (to add module to group)
    const handleGroupDragOver = (e, groupId) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverGroupId(groupId);
        setDragOverPinnedIndex(null);
        // Detect external drag (from Modules page) by checking if moduleCode is in dataTransfer types
        // Note: dataTransfer.types stores keys in lowercase
        const hasModuleData = e.dataTransfer.types.some(t => t.toLowerCase() === 'modulecode');
        if (!draggedGroupModule && !draggedPinnedItem && hasModuleData) {
            setIsExternalModuleDrag(true);
        }
    };

    // Handle drag leave from a group
    const handleGroupDragLeave = (e) => {
        // Only reset if we're leaving the group container, not entering a child
        const relatedTarget = e.relatedTarget;
        const currentTarget = e.currentTarget;
        if (!currentTarget.contains(relatedTarget)) {
            setDragOverGroupId(null);
        }
    };

    // Handle pinned item drag end
    const handlePinnedItemDragEnd = () => {
        setDraggedPinnedItem(null);
        setDragOverPinnedIndex(null);
        setDragOverGroupId(null);
        setIsExternalModuleDrag(false);
    };

    // Handle drop on pinned item (reorder)
    const handlePinnedItemDrop = (e, targetIndex) => {
        e.preventDefault();
        e.stopPropagation();

        if (draggedPinnedItem && draggedPinnedItem.index !== targetIndex) {
            // Get filtered items to find what was actually reordered
            const filteredItems = getFilteredPinnedItems();
            const movedItem = filteredItems[draggedPinnedItem.index];
            const targetItem = filteredItems[targetIndex];

            // Update pinnedItems order
            const newPinned = [...pinnedItems];
            const fromIdx = newPinned.findIndex(i => i.type === movedItem.type && i.id === movedItem.id);
            const toIdx = newPinned.findIndex(i => i.type === targetItem.type && i.id === targetItem.id);
            if (fromIdx !== -1 && toIdx !== -1) {
                const [removed] = newPinned.splice(fromIdx, 1);
                newPinned.splice(toIdx, 0, removed);
                updatePinnedItems(newPinned);
            }

            // Also update itemsOrder (home screen order) for consistency
            try {
                const stored = localStorage.getItem('cabazaDashboardItemsOrder');
                if (stored) {
                    const itemsOrder = JSON.parse(stored);
                    const movedId = `${movedItem.type}:${movedItem.id}`;
                    const targetId = `${targetItem.type}:${targetItem.id}`;
                    const fromOrderIdx = itemsOrder.indexOf(movedId);
                    const toOrderIdx = itemsOrder.indexOf(targetId);
                    if (fromOrderIdx !== -1 && toOrderIdx !== -1) {
                        const [removedItem] = itemsOrder.splice(fromOrderIdx, 1);
                        itemsOrder.splice(toOrderIdx, 0, removedItem);
                        localStorage.setItem('cabazaDashboardItemsOrder', JSON.stringify(itemsOrder));
                        window.dispatchEvent(new CustomEvent('dashboardOrderChanged'));
                    }
                }
            } catch (err) { /* ignore */ }
        }

        handlePinnedItemDragEnd();
    };

    // Handle drop on group (add module to group or move between groups) - LOCAL ONLY, no API
    const handleDropOnGroup = (e, targetGroupId) => {
        e.preventDefault();
        e.stopPropagation();

        // Helper to move module between groups locally
        const moveModuleToGroup = (moduleCode, numericModuleId, sourceGroupId) => {
            let updatedGroups = [...moduleGroups];
            const moduleData = allModules.find(m => (m.code || '').toUpperCase() === moduleCode.toUpperCase());

            // Remove from source group if exists
            if (sourceGroupId) {
                updatedGroups = updatedGroups.map(g => {
                    if (g.id === parseInt(sourceGroupId) || g.id === sourceGroupId) {
                        return {
                            ...g,
                            modules: (g.modules || []).filter(m => (m.moduleid || m.id) !== numericModuleId)
                        };
                    }
                    return g;
                });
            }

            // Add to target group
            updatedGroups = updatedGroups.map(g => {
                if (g.id === targetGroupId) {
                    const existingModules = g.modules || [];
                    if (existingModules.some(m => (m.moduleid || m.id) === numericModuleId)) return g;

                    return {
                        ...g,
                        modules: [...existingModules, {
                            id: numericModuleId,
                            moduleid: numericModuleId,
                            itemid: `${g.id}-${numericModuleId}`,
                            code: moduleCode,
                            name: moduleData?.name,
                            name_fr: moduleData?.name_fr,
                            name_en: moduleData?.name_en,
                            name_nl: moduleData?.name_nl,
                            color: moduleData?.color,
                            icon: moduleData?.icon
                        }]
                    };
                }
                return g;
            });

            setModuleGroups(updatedGroups);
            updateModuleGroups(updatedGroups);
            window.dispatchEvent(new CustomEvent('moduleGroupsChanged'));
        };

        // Check if it's a module being dragged from within a group
        if (draggedGroupModule) {
            const sourceGroupId = draggedGroupModule.groupId;
            const moduleCode = draggedGroupModule.module.code;
            const numericModuleId = getModuleIdMap()[moduleCode];

            if (sourceGroupId == targetGroupId) {
                handleGroupModuleDragEnd();
                handlePinnedItemDragEnd();
                return;
            }

            if (numericModuleId) {
                moveModuleToGroup(moduleCode, numericModuleId, sourceGroupId);
            }

            handleGroupModuleDragEnd();
            handlePinnedItemDragEnd();
            return;
        }

        // Handle pinned module being dropped on a group
        if (draggedPinnedItem && draggedPinnedItem.item.type === 'module') {
            const moduleId = draggedPinnedItem.item.id;
            const moduleCode = getNavToModuleMap()[moduleId];

            if (moduleCode) {
                const numericModuleId = getModuleIdMap()[moduleCode];

                if (numericModuleId) {
                    moveModuleToGroup(moduleCode, numericModuleId, null);
                    // Remove from pinned items
                    const newPinned = pinnedItems.filter(i => !(i.type === 'module' && i.id === moduleId));
                    updatePinnedItems(newPinned);
                }
            }
            handlePinnedItemDragEnd();
            return;
        }

        // Handle module dragged from Modules page (via dataTransfer)
        const moduleCode = e.dataTransfer.getData('moduleCode');
        const sourceGroupId = e.dataTransfer.getData('sourceGroupId') || e.dataTransfer.getData('groupId');

        if (moduleCode) {
            const numericModuleId = getModuleIdMap()[moduleCode];

            if (numericModuleId) {
                if (sourceGroupId && parseInt(sourceGroupId) == targetGroupId) {
                    handlePinnedItemDragEnd();
                    return;
                }

                moveModuleToGroup(moduleCode, numericModuleId, sourceGroupId);
            }
        }

        handlePinnedItemDragEnd();
    };

    // Handle module drag start within a group
    const handleGroupModuleDragStart = (e, groupId, moduleIndex, module) => {
        e.stopPropagation();
        setDraggedGroupModule({ groupId, moduleIndex, module });
        // Also set dataTransfer for cross-component drag (sidebar to Modules page)
        e.dataTransfer.setData('moduleCode', module.code);
        e.dataTransfer.setData('sourceGroupId', groupId.toString());
        e.dataTransfer.setData('moduleId', (module.moduleid || module.id).toString());
        e.dataTransfer.effectAllowed = 'move';
    };

    // Handle module drag over within a group
    const handleGroupModuleDragOver = (e, targetItemId, targetGroupId) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverGroupModuleItemId(targetItemId);
        // Also set group drag over for visual feedback when dragging from different group or external
        if (draggedGroupModule && draggedGroupModule.groupId != targetGroupId) {
            setDragOverGroupId(targetGroupId);
        }
        // Detect external drag (from Modules page)
        const hasModuleData = e.dataTransfer.types.some(t => t.toLowerCase() === 'modulecode');
        if (!draggedGroupModule && !draggedPinnedItem && hasModuleData) {
            setIsExternalModuleDrag(true);
            setDragOverGroupId(targetGroupId);
        }
    };

    // Handle module drag end within a group
    const handleGroupModuleDragEnd = () => {
        setDraggedGroupModule(null);
        setDragOverGroupModuleItemId(null);
        setDragOverGroupId(null);
    };

    // Handle module drop within a group (reorder or move between groups) - LOCAL ONLY, no API
    const handleGroupModuleDrop = (e, targetGroupId, targetIndex, targetModule) => {
        e.preventDefault();
        e.stopPropagation();

        // Helper to move module between groups locally
        const moveModuleToGroupLocal = (moduleCode, numericModuleId, sourceGrpId) => {
            let updatedGroups = [...moduleGroups];
            const moduleData = allModules.find(m => (m.code || '').toUpperCase() === moduleCode.toUpperCase());

            // Remove from source group if exists
            if (sourceGrpId) {
                updatedGroups = updatedGroups.map(g => {
                    if (g.id === parseInt(sourceGrpId) || g.id === sourceGrpId) {
                        return {
                            ...g,
                            modules: (g.modules || []).filter(m => (m.moduleid || m.id) !== numericModuleId)
                        };
                    }
                    return g;
                });
            }

            // Add to target group
            updatedGroups = updatedGroups.map(g => {
                if (g.id === targetGroupId) {
                    const existingModules = g.modules || [];
                    if (existingModules.some(m => (m.moduleid || m.id) === numericModuleId)) return g;

                    return {
                        ...g,
                        modules: [...existingModules, {
                            id: numericModuleId,
                            moduleid: numericModuleId,
                            itemid: `${g.id}-${numericModuleId}`,
                            code: moduleCode,
                            name: moduleData?.name,
                            name_fr: moduleData?.name_fr,
                            name_en: moduleData?.name_en,
                            name_nl: moduleData?.name_nl,
                            color: moduleData?.color,
                            icon: moduleData?.icon
                        }]
                    };
                }
                return g;
            });

            setModuleGroups(updatedGroups);
            updateModuleGroups(updatedGroups);
            window.dispatchEvent(new CustomEvent('moduleGroupsChanged'));
        };

        // Handle external drag from Modules page (via dataTransfer)
        if (!draggedGroupModule) {
            const moduleCode = e.dataTransfer.getData('moduleCode');
            const sourceGroupId = e.dataTransfer.getData('sourceGroupId') || e.dataTransfer.getData('groupId');

            if (moduleCode) {
                const numericModuleId = getModuleIdMap()[moduleCode];

                if (numericModuleId) {
                    if (sourceGroupId && parseInt(sourceGroupId) == targetGroupId) {
                        handleGroupModuleDragEnd();
                        setIsExternalModuleDrag(false);
                        return;
                    }

                    moveModuleToGroupLocal(moduleCode, numericModuleId, sourceGroupId);
                }
            }

            handleGroupModuleDragEnd();
            setIsExternalModuleDrag(false);
            return;
        }

        const sourceGroupId = draggedGroupModule.groupId;
        const moduleCode = draggedGroupModule.module.code;

        // If dropping on a different group, move the module
        if (sourceGroupId != targetGroupId) {
            const numericModuleId = getModuleIdMap()[moduleCode];
            if (numericModuleId) {
                moveModuleToGroupLocal(moduleCode, numericModuleId, sourceGroupId);
            }
            handleGroupModuleDragEnd();
            return;
        }

        // Same group - reorder
        if (draggedGroupModule.module.itemid !== targetModule.itemid) {
            const groupIndex = groupsData.findIndex(g => g.id == targetGroupId);
            if (groupIndex === -1) {
                handleGroupModuleDragEnd();
                return;
            }

            const group = groupsData[groupIndex];
            const modules = [...(group.modules || [])];

            const draggedItemId = draggedGroupModule.module.itemid;
            const targetItemId = targetModule.itemid;

            const sourceIndex = modules.findIndex(m => m.itemid === draggedItemId);
            const destIndex = modules.findIndex(m => m.itemid === targetItemId);

            if (sourceIndex === -1 || destIndex === -1) {
                handleGroupModuleDragEnd();
                return;
            }

            // Reorder the modules
            const [removed] = modules.splice(sourceIndex, 1);
            modules.splice(destIndex, 0, removed);

            // Update local state and save via context
            const newGroupsData = [...groupsData];
            newGroupsData[groupIndex] = { ...group, modules };
            setGroupsData(newGroupsData);
            updateModuleGroups(newGroupsData);
        }

        handleGroupModuleDragEnd();
    };

    // Check if any module has a badge
    const hasModuleBadge = Object.values(moduleCounts).some(c => c > 0);

    // Read dock visibility from preferences
    const { dockVisible } = usePreferences();
    const isDockVisible = dockVisible !== false;

    // Main menu items (top) - conditionally filtered based on dock visibility
    const allMainMenuItems = [
        {
            id: 'home',
            icon: Home,
            label: t.menu?.home || 'Accueil',
        },
        {
            id: 'dashboard',
            icon: BarChart3,
            label: t.menu?.dashboard || 'Dashboard',
            hiddenWhenDock: true,
        },
        {
            id: 'search',
            icon: Search,
            label: t.menu?.search || 'Rechercher',
            hiddenWhenDock: true,
        },
        {
            id: 'modules',
            icon: LayoutGrid,
            label: t.menu?.modules || 'Modules',
            hiddenWhenDock: true,
        },
    ];

    const mainMenuItems = isDockVisible
        ? allMainMenuItems.filter(item => !item.hiddenWhenDock)
        : allMainMenuItems;


    // Check if theme has a background image
    const hasImageBg = currentTheme?.sidebarBgImage;
    const sidebarStyle = hasImageBg ? {
        backgroundImage: `url(${currentTheme.sidebarBgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
    } : {};

    // Check if module is enabled
    const isModuleEnabled = (moduleId) => {
        if (enabledModules[moduleId] === false) return false;
        if (allModules.length > 0) {
            const navMap = getModuleNavMap();
            return allModules.some(m => {
                const code = (m.code || m.CODE || '').toUpperCase();
                const navId = navMap[code] || (m.navid || m.NAVID || code).toLowerCase();
                return navId === moduleId;
            });
        }
        return true;
    };

    // Get group data by id (only pinned and enabled modules)
    const getGroupData = (groupId) => {
        // Use == to handle number/string comparison
        const group = groupsData.find(g => g.id == groupId);
        if (!group) return null;
        return {
            ...group,
            modules: group.modules?.filter(m => {
                const navId = getNavId(m.code);
                // Only show modules that are enabled AND pinned (default to true if isPinned not set)
                const isPinnedValue = m.ispinned ?? m.isPinned ?? m.ISPINNED;
                const isPinned = isPinnedValue === undefined || isPinnedValue === null || isPinnedValue === true || isPinnedValue === 1 || isPinnedValue === '1';
                return isModuleEnabled(navId) && isPinned;
            }).map(m => {
                // Enrich with translation fields from allModules (name_fr, name_en, etc.)
                const fullModule = allModules.find(am =>
                    (am.code || am.CODE || '').toUpperCase() === (m.code || '').toUpperCase()
                );
                return fullModule ? { ...fullModule, ...m } : m;
            }) || []
        };
    };

    // Get modules that are in ANY group (to exclude from individual display)
    const getModulesInGroups = () => {
        const modulesInGroups = new Set();
        // Check all groups - include ALL modules regardless of isPinned status
        // A module in a group should never show as individual, even if unpinned
        groupsData.forEach(group => {
            if (group.modules) {
                group.modules.forEach(m => {
                    const navId = getNavId(m.code);
                    modulesInGroups.add(navId);
                });
            }
        });
        return modulesInGroups;
    };

    // Get filtered pinned items (respecting enabled modules)
    const getFilteredPinnedItems = () => {
        const modulesInGroups = getModulesInGroups();
        const filtered = pinnedItems.filter(item => {
            if (item.type === 'module') {
                // Filter out disabled modules and modules in groups
                return isModuleEnabled(item.id) && !modulesInGroups.has(item.id);
            } else if (item.type === 'group') {
                // Always show pinned groups, even if empty (modules may be hidden by subscription)
                const group = getGroupData(item.id);
                return !!group;
            }
            return false;
        });

        // Sort according to home screen order (itemsOrder) for consistency
        try {
            const stored = localStorage.getItem('cabazaDashboardItemsOrder');
            if (stored) {
                const itemsOrder = JSON.parse(stored);
                filtered.sort((a, b) => {
                    const idA = `${a.type}:${a.id}`;
                    const idB = `${b.type}:${b.id}`;
                    const indexA = itemsOrder.indexOf(idA);
                    const indexB = itemsOrder.indexOf(idB);
                    // Items not in itemsOrder go to the end
                    if (indexA === -1 && indexB === -1) return 0;
                    if (indexA === -1) return 1;
                    if (indexB === -1) return -1;
                    return indexA - indexB;
                });
            }
        } catch (e) { /* keep original order */ }

        return filtered;
    };

    // Get module icon by code - dynamically from DB data
    const getModuleIcon = (code) => {
        const moduleData = findModuleByCode(code, allModules);
        const iconName = moduleData?.icon || moduleData?.ICON;
        return getIconByName(iconName, LayoutGrid);
    };

    // Get nav id from module code (using dynamic map from localStorage)
    const getNavId = (code) => {
        const map = getModuleNavMap();
        return map[code] || code?.toLowerCase();
    };

    return (
        <div
            className="sidebar-menu w-20 h-dvh flex flex-col items-center pt-safe-3 pb-safe-4 shadow-lg relative overflow-hidden"
            style={sidebarStyle}
        >
            {/* Background gradient or image overlay */}
            {hasImageBg ? (
                <div
                    className="absolute inset-0 z-0"
                    style={{ backgroundColor: currentTheme.sidebarBgOverlay || 'rgba(0,0,0,0.4)' }}
                />
            ) : (
                <div className="absolute inset-0 z-0 bg-gradient-to-b from-cabaza-purple to-cabaza-pink" />
            )}

            {/* Home button - fixed at top */}
            <div className="relative z-10 w-full px-2 mb-1">
                <button
                    onClick={() => onNavigate('home')}
                    className={`group relative flex flex-col items-center gap-1 p-2 rounded-xl transition-all duration-200 w-full ${currentPage === 'home'
                        ? 'bg-white/30 backdrop-blur-sm shadow-md'
                        : 'hover:bg-white/10'
                    }`}
                    title={t.menu?.home || 'Accueil'}
                >
                    <Home
                        className={`w-6 h-6 transition-colors ${currentPage === 'home' ? 'text-white' : 'text-white/70 group-hover:text-white'}`}
                    />
                    <span className={`text-[10px] font-medium transition-colors ${currentPage === 'home' ? 'text-white' : 'text-white/70 group-hover:text-white'}`}>
                        {(t.menu?.home || 'Accueil').split(' ')[0]}
                    </span>
                </button>
            </div>

            {/* Main Navigation Menu (scrollable) */}
            <nav
                className={`flex-1 flex flex-col gap-4 w-full px-2 relative z-10 transition-all overflow-y-auto ${isDragOver ? 'bg-white/20 rounded-xl' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {mainMenuItems.filter(item => item.id !== 'home').map((item) => {
                    const Icon = item.icon;
                    const isActive = currentPage === item.id;

                    return (
                        <button
                            key={item.id}
                            onClick={() => onNavigate(item.id)}
                            className={`group relative flex flex-col items-center gap-1 p-3 rounded-xl transition-all duration-200 ${isActive
                                ? 'bg-white/30 backdrop-blur-sm shadow-md'
                                : 'hover:bg-white/10'
                                }`}
                            title={item.label}
                        >
                            <div className="relative">
                                <Icon
                                    className={`w-6 h-6 transition-colors ${isActive ? 'text-white' : 'text-white/70 group-hover:text-white'
                                        }`}
                                />
                                {item.badge > 0 && (
                                    <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-white text-gray-700 text-[9px] rounded-full flex items-center justify-center font-bold shadow-sm">
                                        {item.badge > 9 ? '9+' : item.badge}
                                    </span>
                                )}
                            </div>
                            <span className={`text-[10px] font-medium transition-colors ${isActive ? 'text-white' : 'text-white/70 group-hover:text-white'
                                }`}>
                                {item.label.split(' ')[0]}
                            </span>
                        </button>
                    );
                })}

                {/* Unified Pinned Items (modules and groups) */}
                {(() => {
                    const filteredItems = getFilteredPinnedItems();
                    if (filteredItems.length === 0) return null;

                    return (
                        <div className="bg-white/10 rounded-xl p-1 mt-2">
                            {filteredItems.map((item, index) => {
                                // Find actual index in pinnedItems (not filtered)
                                const actualIndex = pinnedItems.findIndex(p =>
                                    p.type === item.type &&
                                    (item.type === 'module' ? p.id === item.id : p.id === item.id)
                                );
                                const isDragging = draggedPinnedItem?.index === actualIndex;
                                const isLastItem = index === filteredItems.length - 1;
                                // Don't show drop indicator on the item being dragged, or on last item when dragging it
                                // Show drop indicator only when hovering a different item, and hide on last item only if dragging that same last item
                                const isDragOverItem = dragOverPinnedIndex === actualIndex && draggedPinnedItem?.index !== actualIndex && !(isLastItem && draggedPinnedItem?.index === actualIndex);

                                if (item.type === 'module') {
                                    // Render module - get data dynamically from DB
                                    const moduleDef = getModuleDef(item.id);
                                    if (!moduleDef) return null;
                                    const Icon = moduleDef.icon;
                                    const isActive = currentPage === item.id;
                                    const moduleCode = NAV_TO_MODULE_MAP[item.id] || item.id.toUpperCase();
                                    const badgeCount = moduleCounts[moduleCode] || 0;
                                    const hasBadge = badgeCount > 0;

                                    return (
                                        <div
                                            key={`module-${item.id}`}
                                            draggable
                                            onDragStart={(e) => handlePinnedItemDragStart(e, item, actualIndex)}
                                            onDragOver={(e) => handlePinnedItemDragOver(e, actualIndex)}
                                            onDrop={(e) => handlePinnedItemDrop(e, actualIndex)}
                                            onDragEnd={handlePinnedItemDragEnd}
                                            className={`group relative flex flex-col items-center gap-1 p-3 rounded-xl transition-all duration-200 w-full mb-2 cursor-grab active:cursor-grabbing ${isActive
                                                    ? 'bg-white/30 backdrop-blur-sm shadow-md'
                                                    : 'hover:bg-white/10'
                                                } ${isDragging ? 'opacity-50' : ''} ${isDragOverItem ? 'border-2 border-white/50 border-dashed' : ''}`}
                                            title={moduleDef.label}
                                        >
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => onNavigate(item.id)}
                                                onKeyDown={(e) => e.key === 'Enter' && onNavigate(item.id)}
                                                className="flex flex-col items-center gap-1 w-full cursor-pointer"
                                            >
                                                <div className="relative">
                                                    <Icon
                                                        className={`w-6 h-6 transition-colors ${isActive ? 'text-white' : 'text-white/70 group-hover:text-white'}`}
                                                    />
                                                    {hasBadge && (
                                                        <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-white text-gray-700 text-[9px] rounded-full flex items-center justify-center font-bold shadow-sm">
                                                            {badgeCount > 9 ? '9+' : badgeCount}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className={`text-[10px] font-medium transition-colors text-center leading-tight ${isActive ? 'text-white' : 'text-white/70 group-hover:text-white'}`}>
                                                    {moduleDef.label}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                } else if (item.type === 'group') {
                                    // Render group - always show as folder icon, click opens popup
                                    const group = getGroupData(item.id);
                                    if (!group) return null;
                                    const isGroupDragOver = dragOverGroupId === group.id;
                                    const isPopupOpen = openGroupPopup === group.id;

                                    return (
                                        <div
                                            key={`group-${group.id}`}
                                            className={`mb-2 ${isDragging ? 'opacity-50' : ''} ${isDragOverItem ? 'border-2 border-white/50 border-dashed rounded-xl' : ''}`}
                                            draggable
                                            onDragStart={(e) => handlePinnedItemDragStart(e, item, actualIndex)}
                                            onDragOver={(e) => {
                                                if (draggedPinnedItem) {
                                                    handlePinnedItemDragOver(e, actualIndex);
                                                } else {
                                                    handleGroupDragOver(e, group.id);
                                                }
                                            }}
                                            onDragLeave={handleGroupDragLeave}
                                            onDrop={(e) => {
                                                if (draggedGroupModule) {
                                                    handleDropOnGroup(e, group.id);
                                                } else if (draggedPinnedItem) {
                                                    handlePinnedItemDrop(e, actualIndex);
                                                } else {
                                                    handleDropOnGroup(e, group.id);
                                                }
                                            }}
                                            onDragEnd={handlePinnedItemDragEnd}
                                        >
                                            {/* Group folder icon - click to open popup */}
                                            <div
                                                onClick={() => setOpenGroupPopup(isPopupOpen ? null : group.id)}
                                                className={`group relative flex flex-col items-center gap-1 p-3 rounded-xl transition-all duration-200 w-full hover:bg-white/10 cursor-pointer ${isPopupOpen ? 'bg-white/30' : ''} ${isGroupDragOver && (draggedGroupModule || isExternalModuleDrag || !draggedPinnedItem) ? 'bg-white/20 border-2 border-white/50 border-dashed' : ''}`}
                                                title={group[`name_${language}`] || group.name_fr || group.name}
                                            >
                                                <div
                                                    className="w-8 h-8 rounded-lg flex items-center justify-center relative bg-cabaza-purple"
                                                    style={group.color ? { backgroundColor: group.color } : undefined}
                                                >
                                                    <Folder className="w-5 h-5 text-white" />
                                                    {group.modules?.length > 0 && (
                                                        <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-white text-gray-700 text-[9px] rounded-full flex items-center justify-center font-bold shadow-sm">
                                                            {group.modules.length}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className={`text-[10px] font-medium text-center leading-tight max-w-full line-clamp-2 break-words ${isPopupOpen ? 'text-white' : 'text-white/70 group-hover:text-white'}`}>
                                                    {group[`name_${language}`] || group.name_fr || group.name || ''}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            })}
                        </div>
                    );
                })()}

                {/* Drop zone hint - only show when dragging external items, not when reordering or moving between groups */}
                {isDragOver && !draggedPinnedItem && !draggedGroupModule && (
                    <div className="border-2 border-dashed border-white/50 rounded-xl p-4 flex items-center justify-center">
                        <span className="text-white/70 text-xs text-center">Déposer ici</span>
                    </div>
                )}
            </nav>

            {/* Profile button at the bottom - only when dock is hidden */}
            {!isDockVisible && (
                <div className="relative z-10 w-full px-2 pb-2">
                    <button
                        onClick={() => onNavigate('profile')}
                        className={`group relative flex flex-col items-center gap-1 p-3 rounded-xl transition-all duration-200 w-full ${currentPage === 'profile'
                            ? 'bg-white/30 backdrop-blur-sm shadow-md'
                            : 'hover:bg-white/10'
                        }`}
                        title={t.menu?.profile || 'Profil'}
                    >
                        <UserCircle
                            className={`w-6 h-6 transition-colors ${currentPage === 'profile' ? 'text-white' : 'text-white/70 group-hover:text-white'}`}
                        />
                        <span className={`text-[10px] font-medium transition-colors ${currentPage === 'profile' ? 'text-white' : 'text-white/70 group-hover:text-white'}`}>
                            {t.menu?.profile || 'Profil'}
                        </span>
                    </button>
                </div>
            )}

            {/* Group Popup Overlay - Portal to render outside sidebar */}
            {openGroupPopup && createPortal(
                <GroupPopupOverlay
                    group={getGroupData(openGroupPopup)}
                    onClose={() => setOpenGroupPopup(null)}
                    onNavigate={(navId) => {
                        onNavigate(navId);
                        setOpenGroupPopup(null);
                    }}
                    getModuleIcon={getModuleIcon}
                    getNavId={getNavId}
                    currentPage={currentPage}
                    moduleCounts={moduleCounts}
                />,
                document.body
            )}
        </div>
    );
};

/**
 * Group Popup Overlay Component
 * Displays modules in a 3x4 grid overlay
 */
const GroupPopupOverlay = ({ group, onClose, onNavigate, getModuleIcon, getNavId, currentPage, moduleCounts }) => {
    const { language } = useTranslation();
    const popupRef = useRef(null);

    // Module colors (same as PageHeader)
    const MODULE_COLORS = {
        CONTACTS: '#3b82f6',          // blue
        MESSAGES: '#8b5cf6',          // purple
        MAP: '#22c55e',               // green
        AGENDA: '#f97316',            // orange
        EVENTS: '#ec4899',            // pink
        PLACES: '#0ea5e9',            // sky
        SUPPORT: '#06b6d4',           // cyan
        WORK: '#f59e0b',              // amber
        OFFERS: '#10b981',            // emerald
        ADVERTISEMENTS: '#fb923c',    // orange-400
        WEBSITES: '#6366f1',          // indigo
        PRODUCTS: '#f43f5e',          // rose
        KNOWLEDGE: '#a855f7',         // violet
        EXPENSES: '#14b8a6',          // teal
        BOOKS: '#84cc16',             // lime
        MOMENTS: '#f59e0b',           // amber
        CARPOOLING: '#22c55e',        // green
        OCCUPATION_GROUPS: '#64748b', // slate
        PROJECTS: '#8b5cf6',          // purple
        TIME_TRACKING: '#0891b2',     // cyan-600
        MEETINGS: '#d946ef',          // fuchsia
        NEIGHBORHOOD: '#16a34a',      // green-600
        NOTIFICATIONS: '#ef4444',     // red
        ACTIVITIES: '#f97316',        // orange
    };

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (popupRef.current && !popupRef.current.contains(e.target)) {
                // Also check if click is not on the sidebar group button
                const sidebarGroup = document.querySelector(`[title="${group?.name}"]`);
                if (!sidebarGroup?.contains(e.target)) {
                    onClose();
                }
            }
        };

        // Close on Escape key
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose, group?.name]);

    if (!group || !group.modules || group.modules.length === 0) return null;

    return (
        <>
            {/* Semi-transparent backdrop - covers the page content */}
            <div
                className="fixed inset-0 left-20 bg-black/30 z-[9997] pointer-events-auto"
                onClick={onClose}
            />

            {/* Popup container - positioned to the right of sidebar, above backdrop */}
            <div
                ref={popupRef}
                className="fixed left-24 top-1/2 -translate-y-1/2 z-[9999] pointer-events-auto max-h-[80vh] flex flex-col"
            >
                <div
                    className="bg-white rounded-2xl shadow-2xl p-3 min-w-[240px] relative overflow-hidden flex flex-col max-h-full"
                >
                    {/* Left border gradient (same as sidebar) */}
                    <div
                        className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl bg-gradient-to-b from-cabaza-purple to-cabaza-pink"
                    />
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <div
                                className="w-8 h-8 rounded-lg flex items-center justify-center bg-cabaza-purple"
                                style={group.color ? { backgroundColor: group.color } : undefined}
                            >
                                <Folder className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-semibold text-gray-800">{group[`name_${language}`] || group.name_fr || group.name}</span>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>

                    {/* Modules Grid - 3 columns with scroll */}
                    <div className="grid grid-cols-3 gap-2 overflow-y-auto max-h-[60vh] pr-1">
                        {group.modules.map((module) => {
                            // Use module.icon directly if available (from getPeopleModules), otherwise fallback to getModuleIcon
                            const Icon = module.icon ? getIconByName(module.icon, LayoutGrid) : getModuleIcon(module.code);
                            const navId = getNavId(module.code);
                            const isActive = currentPage === navId;
                            const moduleCode = (module.code || '').toUpperCase();
                            const badgeCount = moduleCounts[moduleCode] || 0;
                            const hasBadge = badgeCount > 0;
                            // Use MODULE_COLORS mapping first (consistent colors), fallback to DB color
                            const moduleColor = MODULE_COLORS[module.code] || MODULE_COLORS[module.code?.toUpperCase()] || module.color || '#8b5cf6';

                            // Handle drag start - send data for Home.jsx to handle drop
                            const handleModuleDragStart = (e) => {
                                // Set data in format expected by Home.jsx handleModuleDropFromGroup
                                const dropData = {
                                    type: 'module-from-group',
                                    moduleId: module.moduleid || module.moduleId || module.id,
                                    moduleCode: module.code,
                                    navId: navId,
                                    groupId: group.id
                                };
                                e.dataTransfer.setData('text/plain', JSON.stringify(dropData));
                                // Also set standard format for Modules.jsx compatibility
                                e.dataTransfer.setData('moduleCode', module.code);
                                e.dataTransfer.setData('sourceGroupId', group.id.toString());
                                e.dataTransfer.setData('moduleId', (module.moduleid || module.moduleId || module.id).toString());
                                e.dataTransfer.effectAllowed = 'move';
                            };

                            return (
                                <button
                                    key={module.itemid || module.code}
                                    draggable
                                    onDragStart={handleModuleDragStart}
                                    onClick={() => onNavigate(navId)}
                                    className={`group flex flex-col items-center gap-1.5 p-2 rounded-xl transition-all duration-200 cursor-grab active:cursor-grabbing ${isActive
                                            ? 'bg-gradient-to-br from-cabaza-purple to-cabaza-pink text-white shadow-md'
                                            : 'hover:bg-gray-100 text-gray-700'
                                        }`}
                                >
                                    <div className="relative">
                                        <Icon
                                            className="w-7 h-7 transition-colors"
                                            style={{ color: isActive ? 'white' : moduleColor }}
                                        />
                                        {hasBadge && (
                                            <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-white text-gray-700 text-[9px] rounded-full flex items-center justify-center font-bold shadow-sm">
                                                {badgeCount > 9 ? '9+' : badgeCount}
                                            </span>
                                        )}
                                    </div>
                                    <span className={`text-xs font-medium text-center leading-tight ${isActive ? 'text-white' : 'text-gray-700'}`}>
                                        {module[`name_${language}`] || module.name_fr || module.name || navId}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Menu;
